import L from 'leaflet';
import html2canvas from 'html2canvas';
import type { Position } from '../types';

export interface ThumbnailOptions {
  width?: number;
  height?: number;
  maxZoom?: number;
  padding?: number;
  polylineColor?: string;
  polylineWeight?: number;
}

const DEFAULT_OPTIONS: Required<ThumbnailOptions> = {
  width: 800,
  height: 600,
  maxZoom: 15,
  padding: 10,
  polylineColor: '#2563eb',
  polylineWeight: 17
};

export class RouteThumbnailGenerator {
  private static mapContainer: HTMLElement | null = null;
  private static map: L.Map | null = null;
  private static tileLayer: L.TileLayer | null = null;
  private static isInitialized = false;

  /**
   * Initialize the hidden map instance for thumbnail generation
   */
  static async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Create hidden map container
    this.mapContainer = document.createElement('div');
    this.mapContainer.style.cssText = `
      position: absolute;
      left: -9999px;
      top: -9999px;
      z-index: -1000;
      pointer-events: none;
    `;
    document.body.appendChild(this.mapContainer);

    this.isInitialized = true;
  }

  /**
   * Create a map instance with the specified dimensions
   */
  private static createMap(width: number, height: number, maxZoom: number): L.Map {
    if (!this.mapContainer) {
      throw new Error('ThumbnailGenerator not initialized');
    }

    // Update container size
    this.mapContainer.style.width = `${width}px`;
    this.mapContainer.style.height = `${height}px`;

    // Remove existing map if any
    if (this.map) {
      this.map.remove();
    }

    // Create new map
    this.map = L.map(this.mapContainer, {
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true, // Better performance for screenshots
      zoomSnap: 0.1,
      zoomDelta: 0.1
    });

    // Add tile layer (using OpenStreetMap)
    this.tileLayer = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: maxZoom,
      attribution: '', // Remove for thumbnails
      crossOrigin: 'anonymous' // Important for canvas capture
    }).addTo(this.map);

    return this.map;
  }

  /**
   * Wait for all tiles to load
   */
  private static waitForTiles(timeoutMs: number = 3000): Promise<void> {
    return new Promise((resolve) => {
      if (!this.map) {
        resolve();
        return;
      }

      let tilesLoading = 0;
      let tilesLoaded = 0;
      let resolved = false;

      const checkComplete = () => {
        if (resolved) return;
        if (tilesLoading === tilesLoaded && tilesLoading > 0) {
          resolved = true;
          resolve();
        }
      };

      // Track tile loading
      this.tileLayer?.on('loading', () => {
        tilesLoading++;
      });

      this.tileLayer?.on('load', () => {
        tilesLoaded++;
        setTimeout(checkComplete, 100);
      });

      // Fallback timeout
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      }, timeoutMs);

      // Initial check in case tiles are already loaded
      setTimeout(checkComplete, 100);
    });
  }

  /**
   * Generate a route thumbnail
   */
  static async generateThumbnail(
    coordinates: Position[],
    options: ThumbnailOptions = {}
  ): Promise<Blob> {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    if (coordinates.length < 2) {
      throw new Error('At least 2 coordinates required for route thumbnail');
    }

    await this.initialize();

    try {
      // Create map with specified dimensions
      const map = this.createMap(opts.width, opts.height, opts.maxZoom);

      // Convert coordinates to Leaflet format
      const latLngs: L.LatLngExpression[] = coordinates.map(coord => [coord.lat, coord.lng]);

      // Add route polyline
      const polyline = L.polyline(latLngs, {
        color: opts.polylineColor,
        weight: opts.polylineWeight,
        opacity: 0.9,
        lineCap: 'round',
        lineJoin: 'round'
      }).addTo(map);

      // Add start marker (green)
      const startIcon = L.divIcon({
        className: 'route-start-marker',
        html: `<div style="
          width: 12px; height: 12px; 
          background: #22c55e; 
          border: 2px solid white; 
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        "></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      L.marker(latLngs[0], { icon: startIcon }).addTo(map);

      // Add end marker (red)
      const endIcon = L.divIcon({
        className: 'route-end-marker',
        html: `<div style="
          width: 12px; height: 12px; 
          background: #ef4444; 
          border: 2px solid white; 
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        "></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      L.marker(latLngs[latLngs.length - 1], { icon: endIcon }).addTo(map);

      // Fit bounds with configurable padding
      const bounds = polyline.getBounds();
      map.fitBounds(bounds, { 
        padding: [opts.padding, opts.padding],
        maxZoom: opts.maxZoom
      });

      // Wait for tiles to load
      await this.waitForTiles();

      // Add a small delay to ensure everything is rendered
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate screenshot using html2canvas
      const canvas = await html2canvas(this.mapContainer!, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        backgroundColor: '#f8fafc',
        logging: false,
        width: opts.width,
        height: opts.height
      });

      // Convert canvas to blob
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail blob'));
          }
        }, 'image/png', 0.9);
      });

    } catch (error) {
      console.error('Failed to generate route thumbnail:', error);
      throw error;
    }
  }

  /**
   * Cleanup resources
   */
  static cleanup(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    if (this.mapContainer && this.mapContainer.parentNode) {
      this.mapContainer.parentNode.removeChild(this.mapContainer);
      this.mapContainer = null;
    }
    this.tileLayer = null;
    this.isInitialized = false;
  }
}

/**
 * Utility function to generate Supabase imgproxy thumbnail URLs
 */
export const getRouteThumbnailUrl = (
  supabaseUrl: string,
  imagePath: string, 
  size: 'small' | 'medium' | 'large' | 'list' = 'medium'
): string => {
  const baseUrl = `${supabaseUrl}/storage/v1/render/image/public/route-thumbnails/${imagePath}`;
  
  const sizeParams = {
    list: 'width=80&height=60&resize=fill&quality=75',
    small: 'width=200&height=150&resize=cover&quality=80',
    medium: 'width=400&height=300&resize=cover&quality=85',
    large: 'width=800&height=600&resize=cover&quality=90'
  };
  
  return `${baseUrl}?${sizeParams[size]}`;
};
